/**
 * @module leadGeneration.mjs
 * @description This is a store for the "Eigenrecherche" feature. It is used to create a list of businessPartner leads based on a googlePlaces search
 */

import { defineStore } from "pinia";

import { useUserStore } from "./user";

import { fetchFromBackend } from "./helpers/backend.mjs";

export const useLeadMatchingStore = defineStore("leadMatching", {
  state: () => {
    return {
      leadLists: [], // lists of businessPartners, typically based on a google places search
      lastFormLead: null,
      matchLeadsIsAvailable: false, // null = noch nicht geprüft, true = erreichbar, false = nicht erreichbar (z.B. aufgrund von Backend-Problemen)
    }
  },
  getters: {
    businessPartnerList: (state) => {
      const userStore = useUserStore();
      const businessPartnerListExists = state.leadLists.find((list) => list.signature === userStore.currentSelection.leadMatching.signature);
      return (businessPartnerListExists) ? businessPartnerListExists : { summary: '', data: [], size: 0 };
    }
  },
  actions: {
    setLastFormLead(leadObj) {
      this.lastFormLead = { ...leadObj };
    },
    /** nutzt die Hilfsfunktion matchLeadsIsAvailable, um festzustellen, ob die Matching Funktion grundsätzlich verfügbar ist    */
    async checkMatchLeadsIsAvailable() {
      // set to null first so UI can show the "prüfen" loading indicator
      this.matchLeadsIsAvailable = null;
      try {
        this.matchLeadsIsAvailable = await fetchFromBackend('businessPartners/v1/matchLeadsIsAvailable', {});
      } catch (err) {
        console.error('checkMatchLeadsIsAvailable failed', err);
        this.matchLeadsIsAvailable = false;
      }
    },
    /** match a Formular Input lead against the businessPartner index to see if we alreaddy know the businessPartner
     * @async
     * @param {Object} lead - A lead object as returned by googlePlaces
     * @returns {Promise<Object>} An array of 0 or more businessPartners that match the lead with confidence and distance
    */
    async matchLeadAgainstBusinessPartnerIndex(lead) {
      const userStore = useUserStore();
      userStore.currentSelection.leadMatching.isLoading = true;

      const endpoint = "businessPartners/v1/matchLeads";
      // Build address only from available parts to avoid stray commas/spaces
      const addressElemets = [[lead.street, lead.houseNumber].filter(Boolean).join(" "), [lead.postalCode, lead.city].filter(Boolean).join(" "), "Deutschland"].filter(Boolean);
      const payload = {
        businessPartner: {
          id: '-1', // omit unknown id to satisfy schema (id expects string)
          name: lead.name,
          address: addressElemets.join(", "),
          urls: lead.url ? [lead.url] : []
        },
        fields: ["id", "meta", "name", "address", "phoneNumbers", "urls"],
        options: {
          maxCandidates: 5,
          maxDistance: 15, distance: "km",
          includeRelations: {
            deprecated: "replace", contentPartner: "replace"
          },
          minConfidence: 0.3,
        }
      };
      const response = await fetchFromBackend(endpoint, payload);

      userStore.currentSelection.leadMatching.isLoading = false;

      // Baue die leadList analog zu leadGeneration
      const leadListEntry = {
        signature: lead.name, // oder eine andere eindeutige Signatur
        data: response.candidates || [],
        update: Date.now(),
        isLoading: false
      };

      // Fallback auf unnormalisierte Adresse, wenn keine Normalisierung vorhanden ist
      for (const candidate of leadListEntry.data) {
        candidate.meta.normalization = candidate.meta.normalization || {};
        if (candidate.address && !candidate.meta?.normalization?.address) candidate.meta.normalization.address = candidate.address;
        if (candidate.phoneNumbers && !candidate.meta?.normalization?.phoneNumbers) candidate.meta.normalization.phoneNumbers = candidate.phoneNumbers;
      }

      // Optional: processed-Flag für jedes Lead
      leadListEntry.data.forEach(candidate => candidate.processed = false);

      this.leadLists.push(leadListEntry);
      console.log('matchLeadAgainstBusinessPartnerIndex: candidates', leadListEntry.data);
      return leadListEntry;
    },
    /** get the status for all matched businessPartners (candidates) from the mba database
     *  and return the one with the highest confidence that exists in the database
     * @async
     * @param {Object} lead - A lead object as returned by googlePlaces
     * @returns {Promise<Object>} The businessPartner object with the status
    */
    async getBusinessPartnerStatus(lead) {
      const userStore = useUserStore();
      const endpoint = "businessPartners/v1/getBusinessPartnerStatus";

      for (const candidate of lead.candidates || []) {
        const payload = {
          id: userStore.id,
          businessPartner: {
            gpNo: candidate.meta.gpNo,
          },
          filter: {
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id
          }
        }
        const response = await fetchFromBackend(endpoint, payload);
        if (response && !response.errors) return Object.assign(candidate, response);
      }
    },
    async convertLeads() {
      const userStore = useUserStore();
      const endpoint = "businessPartners/v1/createFromLeads";

      // depending on whether the lead is a google lead or a businessPartner lead, we need to set the idGoogle or idBusinessPartner
      console.log('convertLeads: lastFormLead', this.lastFormLead);
      const lead = {
        idGoogle: null,
        idBusinessPartner: null,
        name: this.lastFormLead.name,
        address: {
          name: `${this.lastFormLead.street} ${this.lastFormLead.houseNumber}, ${this.lastFormLead.postalCode} ${this.lastFormLead.city}`,
        },
        // Einzelne Formular-Telefonnummer in Array wandeln (Backend erwartet Array von Nummern)
        phoneNumbers: this.lastFormLead?.phoneNumber ? [this.lastFormLead.phoneNumber] : null,
        // Formular-URL immer als Array senden (wird im Backend via join('|') genutzt)
        urls: this.lastFormLead?.url ? [this.lastFormLead.url] : [],
        match: null,
        // Für Formular-Leads Kennzeichnung + strukturierte Adressdaten mitgeben
        selection: {
          form: true,
          address: {
            street: this.lastFormLead.street,
            houseNumber: this.lastFormLead.houseNumber,
            postalCode: this.lastFormLead.postalCode,
            city: { name: this.lastFormLead.city },
          },
        },
        updateBusinessPartner: null, // this is used to update an existing businessPartner with data from the google lead
      }
      // see if its an interesting match to return it as feedback for the machine learning model
      // if (entry.match && entry.convertToBusinessPartner === 'allow-both') {
      //   lead.feedback = {
      //     businessPartner_1: {
      //       name: entry.name,
      //       address: entry.meta.googlePlaces?.address?.replaceAll(", Germany", ", Deutschland"),
      //       phoneNumbers: (entry.meta.googlePlaces?.phoneNumbers) ? entry.meta.googlePlaces.phoneNumbers : null,
      //       urls: (entry.meta.googlePlaces?.urls) ? entry.meta.googlePlaces.urls : null,
      //       meta: { idGoogle: entry.meta.googlePlaces.id }
      //     },
      //     businessPartner_2: {
      //       name: entry.match?.meta?.normalization?.name,
      //       address: entry.match?.meta?.normalization?.address,
      //       phoneNumbers: (entry.match?.meta?.normalization?.phoneNumbers) ? entry.match.meta.normalization.phoneNumbers : null,
      //       urls: (entry.match?.meta?.normalization?.urls) ? entry.match?.meta?.normalization?.urls : null,
      //       meta: { gpNo: entry.match?.meta?.gpNo }
      //     },
      //     isDuplicate: (entry.selection.businessPartner) ? true : false, // if the existing businessPartner was selected, it is assumed that this is based on a match (consider it a duplicate)
      //   }
      // }

      const payload = {
        id: userStore.id,
        campaign: {
          id: userStore.currentSelection.campaign.id,
        },
        list: {
          id: -1,
          name: 'SMA Formular',
        },
        leads: [lead],
      }
      console.log('convertLeads: payload', payload);
      const response = await fetchFromBackend(endpoint, payload);
      return response;
    },
    async removeQuery(query) {
      console.log('removeQuery: query', query);
      this.leadLists = this.leadLists.filter((list) => list.signature !== query);
    },
  },
});